import type { Money } from '@libs/trip-planner-interfaces';

import { ItinerarySegmentType } from './itinerarySegments';

export interface TripSummarySegmentBase {
  title: string;
  price: TripSummarySegmentPrice[];
  images?: string[];
}

export interface TripSummaryTransportSegmentBase
  extends TripSummarySegmentBase {
  date: string;
  time: string;
  carrier?: string;
  transportDirection?: string;
}

export interface TripSummaryAccommodationSegmentBase
  extends TripSummarySegmentBase {
  date: string;
  location: string | undefined;
}

export interface TripSummarySegmentPrice {
  description: string;
  value: string;
  amount: number;
  type: 'ingredient' | 'addon';
}

export const enum TripSummaryPriceType {
  Total = 'total',
  TotalGross = 'totalGross',
  TotalAlternative = 'totalAlternative',
  TotalGrossAlternative = 'totalGrossAlternative',
}

export const enum TripSegmentDetailType {
  Direction = 'direction',
  Carrier = 'carrier',
  Stops = 'stops',
  Tickets = 'tickets',
  Nights = 'nights',
  Title = 'title',
  Date = 'date',
}

export interface TripSummaryPrice {
  totalPrice: Money | undefined;
  grossPrice: Money | undefined;
  priceType: TripSummaryPriceType;
}

export interface TripSummaryFlightSegment
  extends TripSummaryTransportSegmentBase {
  summaryType: ItinerarySegmentType.Flight;
  stops: string | undefined;
}

export interface TripSummaryFerrySegment
  extends TripSummaryTransportSegmentBase {
  summaryType: ItinerarySegmentType.Ferry;
}

export interface TripSummaryHotelSegment
  extends TripSummaryAccommodationSegmentBase {
  summaryType: ItinerarySegmentType.Accommodation;
  nights: number;
}

export interface TripSummaryActivitySegment
  extends TripSummaryAccommodationSegmentBase {
  summaryType: ItinerarySegmentType.Activity;
  numberOfTickets: number;
}

export type TripSummarySegment =
  | TripSummaryFlightSegment
  | TripSummaryFerrySegment
  | TripSummaryHotelSegment
  | TripSummaryActivitySegment;

export function isTransportSegment(
  segment: TripSummarySegment
): segment is TripSummaryFlightSegment | TripSummaryFerrySegment {
  return (
    segment.summaryType === ItinerarySegmentType.Flight ||
    segment.summaryType === ItinerarySegmentType.Ferry
  );
}

export function isFlightSegment(
  segment: TripSummarySegment
): segment is TripSummaryFlightSegment {
  return segment.summaryType === ItinerarySegmentType.Flight;
}

export function isHotelSegment(
  segment: TripSummarySegment
): segment is TripSummaryHotelSegment {
  return segment.summaryType === ItinerarySegmentType.Accommodation;
}

export function isActivitySegment(
  segment: TripSummarySegment
): segment is TripSummaryActivitySegment {
  return segment.summaryType === ItinerarySegmentType.Activity;
}
